import * as d3 from "d3";
import { $ } from "jquery";
import d3cloud from "d3-cloud";
import { Random, MersenneTwister19937 } from "random-js";
import type { NxCell } from "@qlik/api/qix";
import type { SnWordCloudLayout } from "../tsp-output/types";
import defaults from "../tsp-output/defaults.json";

export function resolveLayoutDefaults(layout: SnWordCloudLayout) {
  return {
    ...layout,
    ScaleColor: layout.ScaleColor ?? defaults.ScaleColor,
    Orientations: layout.Orientations ?? defaults.Orientations,
    MinSize: layout.MinSize ?? defaults.MinSize,
    MaxSize: layout.MaxSize ?? defaults.MaxSize,
    RadStart: layout.RadStart ?? defaults.RadStart,
    RadEnd: layout.RadEnd ?? defaults.RadEnd,
    Scale: layout.Scale ?? defaults.Scale,
    customRange: layout.customRange ?? defaults.customRange,
    customRangeFrom: layout.customRangeFrom ?? defaults.customRangeFrom,
    customRangeTo: layout.customRangeTo ?? defaults.customRangeTo,
    colorFrom: layout.colorFrom ?? layout.customRangeFrom ?? defaults.customRangeFrom,
    colorTo: layout.colorTo ?? layout.customRangeTo ?? defaults.customRangeTo,
  };
}

export interface SelectionController {
  beginSelections(root: Element | null): void;
  selectValues(dimension: number, values: number[], toggle: boolean): void;
}

interface CloudWord extends d3cloud.Word {
  text: string;
  elemNumber: number;
  value: number;
}

const RANDOM_SEED = 0x12345;

function draw(
  words: CloudWord[],
  layout: ReturnType<typeof resolveLayoutDefaults>,
  element: HTMLElement,
  component: SelectionController,
  scaleColor: string[],
  id: string,
  width: number,
  height: number,
  resolve: () => void
) {
  const data = words.map((d) => ({
    text: d.text,
    elemNumber: d.elemNumber,
    value: d.value,
    size: d.size,
    x: d.x,
    y: d.y,
    rotate: d.rotate,
  }));

  const maxWordValue = data.reduce(
    (accum, word) => Math.max(accum, word.value),
    0
  );
  let fill = null;
  if (layout.customRange) {
    fill = d3
      .scaleLinear<string>()
      .interpolate(d3.interpolateHcl)
      .domain([1, maxWordValue])
      .range([layout.colorTo.color, layout.colorFrom.color]);
  } else {
    fill = d3
      .scaleLinear<string>()
      .interpolate(d3.interpolateHcl)
      .domain(
        [...Array(scaleColor.length).keys()].map(
          (i) => ((i + 1) / scaleColor.length) * maxWordValue
        )
      )
      .range(scaleColor);
  }

  const svg = d3
    .select(element)
    .select(`#${id}`)
    .append("svg")
    .attr("width", width)
    .attr("height", height)
    .attr("class", "wordcloud")
    .append("g")
    .attr("transform", `translate(${[width / 2, height / 2].join(",")})`);

  svg
    .selectAll("text")
    .data(data)
    .enter()
    .append("text")
    .style("fill", (word) => fill(word.value))
    .attr("class", "selectable")
    .attr("data-value", (word) => word.elemNumber)
    .attr("text-anchor", "middle")
    .attr("transform", (word) =>
      `translate(${[word.x, word.y].join(",")}) rotate(${word.rotate || 0})`
    )
    .style("font-size", (word) => `${word.size}px`)
    .text((word) => word.text)
    .append("svg:title")
    .text((word) => `${word.text}:${word.value}`);

  //when an item is clicked, add it to the selected values and show the Sense UI for selections
  $(element)
    .find(".selectable")
    .on("click", (event) => {
      const target = event.target;
      if (!(target instanceof SVGElement)) {
        return;
      }
      const word = d3.select<SVGElement, CloudWord>(target).datum();
      const valueFromHyperCube = layout.qHyperCube.qDataPages?.[0]?.qMatrix?.find(
        (row: NxCell[]) => row[0].qText === word.text
      );
      if (!valueFromHyperCube || valueFromHyperCube[0].qIsNull) {
        return;
      }
      if (!target.hasAttribute("data-value")) {
        return;
      }

      if ($(target).hasClass("selected")) {
        const selClass = $(target).attr("class") ?? "";
        $(target).attr("class", selClass.replace("selected", "selectable"));
      } else {
        $(target).attr("class", "selected");
      }

      component.beginSelections(element.querySelector(".wordcloud"));

      const value = parseInt(target.getAttribute("data-value") ?? "", 10);
      component.selectValues(0, [value], true);
    });

  resolve();
}

const wordcloud = () => ({
  Id: "",
  Width: 0,
  Height: 0,
  fill: null,

  go(
    words: CloudWord[],
    layout: ReturnType<typeof resolveLayoutDefaults>,
    element: HTMLElement,
    component: SelectionController
  ) {
    const max = Math.max(layout.qHyperCube.qMeasureInfo?.[0]?.qMax ?? 1, 1); // log scale should never cross zero
    const min = Math.max(layout.qHyperCube.qMeasureInfo?.[0]?.qMin ?? 1, 1); // log scale should never cross zero

    const scaleFunction = layout.Scale === "log" ? d3.scaleLog : d3.scaleLinear;

    const scale = scaleFunction()
      .domain([min, max])
      .rangeRound([layout.MinSize, layout.MaxSize]);
    const from = Math.max(-90, Math.min(90, layout.RadStart));
    const to = Math.max(-90, Math.min(90, layout.RadEnd));
    const scaleRotate = d3
      .scaleLinear()
      .domain([0, layout.Orientations - 1])
      .range([from, to]); // Input [0,1] convert into output [-90,90]
    const random = new Random(MersenneTwister19937.seed(RANDOM_SEED + words.length));
    return new Promise<void>((resolve) => {
      d3cloud<CloudWord>()
        .size([this.Width, this.Height])
        .words(words)
        .padding(5)
        .timeInterval(10)
        .random(() => random.real(0, 1))
        .rotate(() => scaleRotate(
          Math.round(random.real(0, 1) * (layout.Orientations - 1))
        ))
        .fontSize((word) => scale(word.value))
        .font("QlikView Sans")
        .on("end", (placedWords) => {
          draw(
            placedWords,
            layout,
            element,
            component,
            layout.ScaleColor,
            this.Id,
            this.Width,
            this.Height,
            resolve
          );
        })
        .start();
    });
  },

  id(value: string) {
    this.Id = value;
    return this;
  },
  width(value: number) {
    this.Width = value;
    return this;
  },

  height(value: number) {
    this.Height = value;
    return this;
  },
});

async function paint(
  element: HTMLElement,
  inputLayout: SnWordCloudLayout,
  component: SelectionController
) {
  const layout = resolveLayoutDefaults(inputLayout);
  const qHyperCube = layout.qHyperCube;
  const matrix = qHyperCube.qDataPages?.[0]?.qMatrix;
  if (!matrix || !qHyperCube.qMeasureInfo?.[0]) {
    return;
  }
  const size = element.getBoundingClientRect();
  if (size.height < 10 || size.width < 10) {
    return;
  }
  const id = `wordcloud_${layout.qInfo?.qId ?? "chart"}`;

  element.textContent = "";

  $("<div />")
    .attr("id", id)
    .width(size.width)
    .height(size.height)
    .appendTo(element);

  const words: CloudWord[] = matrix.flatMap((row: NxCell[]) => {
    const text = row[0]?.qText;
    const elemNumber = row[0]?.qElemNumber;
    const value = row[1]?.qNum;
    return text !== undefined && elemNumber !== undefined && value !== undefined
      ? [{ text, elemNumber, value }]
      : [];
  });

  const cloud = wordcloud().id(id).width(size.width).height(size.height);
  return cloud.go(words, layout, element, component);
}

export default paint;
